import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabaseClient';
import Navbar from '../components/Navbar';
import { BeakerIcon } from '@heroicons/react/24/outline';
import PatternDiscovery from '../components/admin/PatternDiscovery';

interface TabConfig {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  component: React.ComponentType;
}

export default function FlowDocsLabsPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pattern-discovery');
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    checkAuthorization();
  }, [user]);

  const checkAuthorization = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    // TEMPORARY: Hardcode john@flowlogicllc.com to bypass RLS check
    if (user.email === 'john@flowlogicllc.com') {
      console.log('Labs access granted for john@flowlogicllc.com (temporary bypass)');
      setIsAuthorized(true);
      setLoading(false);
      return;
    }

    // Check system_admins table
    try {
      const { data: systemAdmin, error } = await supabase
        .from('system_admins')
        .select('email')
        .eq('email', user.email)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking admin status:', error);
        alert('Error checking admin permissions. Please contact support.');
        navigate('/dashboard');
        return;
      }

      if (systemAdmin) {
        setIsAuthorized(true);
      } else {
        console.log('User not authorized for FlowDocs Labs:', user.email);
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to check admin authorization:', error);
      alert('Failed to verify admin permissions. Please contact support.');
      navigate('/dashboard');
    }

    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navbar onSignOut={signOut} />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navbar onSignOut={signOut} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-red-50 p-8 rounded-lg text-center">
            <h2 className="text-2xl font-bold text-red-800 mb-4">Access Denied</h2>
            <p className="text-red-600">You don't have permission to access FlowDocs Labs.</p>
          </div>
        </div>
      </div>
    );
  }

  const tabs: TabConfig[] = [
    {
      id: 'pattern-discovery',
      label: 'Pattern Discovery',
      icon: BeakerIcon,
      component: PatternDiscovery
    }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || PatternDiscovery;

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar onSignOut={signOut} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <BeakerIcon className="h-8 w-8 text-purple-600" />
            <h1 className="text-3xl font-bold text-gray-900">
              FlowDocs Labs
            </h1>
          </div>
          <p className="text-gray-600 mt-2">Experimental features and advanced tooling</p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white shadow-sm rounded-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-6 border-b-2 font-medium text-sm flex items-center gap-2 ${
                      activeTab === tab.id
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          <ActiveComponent />
        </div>
      </div>
    </div>
  );
}
